import { MoreOutlined } from '@ant-design/icons';
import { Alert, App, Avatar, Button, Dropdown, Table, Tag } from 'antd';
import { avatarColor, initials } from './lib';
export function PageHeader({ title, description, actions }) {
    return (<div className="page-header">
      <div>
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>);
}
export function Panel({ title, description, extra, children, flush, className }) {
    return (<section className={['panel', className].filter(Boolean).join(' ')}>
      {(title || extra) && (<div className="panel-head">
          <div>
            {title && <h3>{title}</h3>}
            {description && <p>{description}</p>}
          </div>
          {extra}
        </div>)}
      {flush ? children : <div className="panel-body">{children}</div>}
    </section>);
}
export function StatCard({ label, value, icon, tone, foot, loading }) {
    return (<section className="panel stat" aria-busy={loading || undefined}>
      <div className="stat-top">
        <span>{label}</span>
        <span className={`stat-icon tone-${tone}`}>{icon}</span>
      </div>
      <div className="stat-value">{loading ? <Bone width={72} height={28}/> : value}</div>
      <div className="stat-foot">{loading ? <Bone width="70%" height={12}/> : foot}</div>
    </section>);
}
// ------------------------------------------------------------------ skeletons
/** One shimmering placeholder bar (or circle) standing in for content that is loading. */
export function Bone({ width = '100%', height = 12, round, style }) {
    return <span className={round ? 'bone round' : 'bone'} style={{ width, height, ...style }} aria-hidden/>;
}
/** A person (avatar, name, detail line) while it loads. */
export function PersonSkeleton({ size = 36 }) {
    return (<div className="person">
      <Bone width={size} height={size} round/>
      <div className="bone-lines">
        <Bone width={140} height={12}/>
        <Bone width={190} height={10}/>
      </div>
    </div>);
}
/** Rows of a feed list (icon or avatar, two lines, something on the right) while they load. */
export function ListSkeleton({ rows = 4, avatar = 34, trailing = true }) {
    return (<ul className="feed" aria-busy>
      {Array.from({ length: rows }, (_, index) => (<li key={index} style={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <PersonSkeleton size={avatar}/>
          {trailing && <Bone width={64} height={20} style={{ borderRadius: 6 }}/>}
        </li>))}
    </ul>);
}
/** A settings form (label column, fields, actions) while it loads. */
export function FormSkeleton({ sections = 3 }) {
    return (<div aria-busy>
      {Array.from({ length: sections }, (_, index) => (<div className="form-section" key={index}>
          <div className="bone-lines">
            <Bone width={110} height={13}/>
            <Bone width="90%" height={10}/>
            <Bone width="70%" height={10}/>
          </div>
          <div className="bone-lines" style={{ gap: 10 }}>
            <Bone width={90} height={11}/>
            <Bone height={36} style={{ borderRadius: 8 }}/>
            {index % 2 === 0 && (<div className="form-row">
                <Bone height={36} style={{ borderRadius: 8 }}/>
                <Bone height={36} style={{ borderRadius: 8 }}/>
              </div>)}
          </div>
        </div>))}
      <div className="form-actions">
        <Bone width={120} height={36} style={{ borderRadius: 8 }}/>
        <Bone width={120} height={36} style={{ borderRadius: 8 }}/>
      </div>
    </div>);
}
const BONE_WIDTHS = ['72%', '56%', '84%', '64%', '48%', '78%'];
function skeletonCell(kind, row) {
    if (kind === 'person')
        return <PersonSkeleton />;
    if (kind === 'tag')
        return <Bone width={70} height={22} style={{ borderRadius: 6 }}/>;
    if (kind === 'actions')
        return <Bone width={28} height={28} style={{ borderRadius: 8, marginLeft: 'auto' }}/>;
    if (kind === 'lines') {
        return (<div className="bone-lines">
        <Bone width={BONE_WIDTHS[row % BONE_WIDTHS.length]} height={12}/>
        <Bone width="60%" height={10}/>
      </div>);
    }
    return <Bone width={BONE_WIDTHS[(row + 2) % BONE_WIDTHS.length]} height={12}/>;
}
/**
 * A table that shows skeleton rows shaped like its columns on the first load, and keeps showing the rows
 * it has while it loads again (no spinner over data the admin is reading).
 */
export function DataTable({ loading, dataSource, columns, skeletonRows = 6, ...props }) {
    const first = loading && (!dataSource || dataSource.length === 0);
    if (first) {
        const placeholders = Array.from({ length: skeletonRows }, (_, index) => ({ __skeleton: index }));
        return (<Table {...props} className={['table-skeleton', props.className].filter(Boolean).join(' ')} rowKey="__skeleton" dataSource={placeholders} pagination={false} aria-busy columns={columns.map((column) => ({
                title: column.title,
                width: column.width,
                align: column.align,
                render: (_value, row) => skeletonCell(column.skeleton ?? 'text', row.__skeleton)
            }))}/>);
    }
    return <Table {...props} dataSource={dataSource} columns={columns}/>;
}
/** The ⋮ button at the end of a table row. Its actions open in a menu; risky ones ask in a dialog first. */
export function RowActions({ actions, label = 'Actions' }) {
    const { modal } = App.useApp();
    const shown = actions.filter(Boolean);
    if (shown.length === 0)
        return null;
    const run = (action) => {
        if (!action.confirm)
            return void action.onClick();
        modal.confirm({
            title: action.confirm.title,
            content: action.confirm.description,
            okText: action.confirm.okText,
            okButtonProps: { danger: action.danger },
            cancelText: 'Cancel',
            centered: true,
            onOk: () => action.onClick()
        });
    };
    return (<Dropdown trigger={['click']} placement="bottomRight" overlayClassName="row-actions-menu" menu={{
            items: shown.map((action) => ({
                key: action.key,
                icon: action.icon,
                danger: action.danger,
                disabled: action.disabled,
                label: (<span className="row-action">
              {action.label}
              {action.hint && <small>{action.hint}</small>}
            </span>)
            })),
            onClick: ({ key, domEvent }) => {
                domEvent.stopPropagation();
                const action = shown.find((item) => item.key === key);
                if (action && !action.disabled)
                    run(action);
            }
        }}>
      <Button type="text" size="small" className="row-actions-button" icon={<MoreOutlined />} aria-label={label} onClick={(event) => event.stopPropagation()}/>
    </Dropdown>);
}
export function Person({ name, detail, badge, size = 36 }) {
    return (<div className="person">
      <Avatar size={size} style={{ background: avatarColor(name), flex: 'none', fontWeight: 600, fontSize: size * 0.38 }}>
        {initials(name)}
      </Avatar>
      <div className="person-text">
        <strong>
          {name}
          {badge}
        </strong>
        {detail && <span>{detail}</span>}
      </div>
    </div>);
}
export function ErrorNotice({ error }) {
    if (!error)
        return null;
    return <Alert type="error" showIcon title={error} style={{ marginBottom: 20 }}/>;
}
export function ActiveTag({ active, on = 'Active', off = 'Disabled' }) {
    return active ? (<Tag color="success" variant="filled">
      <span className="status-dot"/>
      {on}
    </Tag>) : (<Tag variant="filled">{off}</Tag>);
}
const SUBSCRIPTION_TAGS = {
    scheduled: { color: 'processing', label: 'Scheduled' },
    active: { color: 'success', label: 'Active' },
    expiring: { color: 'warning', label: 'Expiring soon' },
    grace: { color: 'orange', label: 'In grace period' },
    expired: { color: 'default', label: 'Expired' }
};
export function SubscriptionTag({ state }) {
    const tag = SUBSCRIPTION_TAGS[state];
    return (<Tag color={tag.color} variant="filled">
      {tag.label}
    </Tag>);
}
const PLAN_COLORS = { trial: 'default', basic: 'blue', pro: 'purple', complimentary: 'gold' };
/** A subscription's plan: the plan's name when the code is a known plan (pro-12 → Pro), else the code as typed. */
export function PlanTag({ plan, name }) {
    const family = plan.toLowerCase().split(/[-\s]/)[0] ?? '';
    return (<Tag color={PLAN_COLORS[family] ?? 'default'} variant="filled" style={{ textTransform: name ? undefined : 'capitalize', fontWeight: 500 }} title={plan}>
      {name ?? plan}
    </Tag>);
}
